import type { SupabaseClient } from "@supabase/supabase-js";

// The connected flow's read and write contracts: `management_schedule_publish_review` and
// `publish_shift_template_week` (migration 20260909120000_schedule_connected_flow.sql).
//
// Why these exist next to the shipped `publishShiftTemplate` adapter rather than replacing it:
// the Builder's positional receipt is still the shipped path, and this lane is expand only.
// What is new is the pair, and one gate shared by both surfaces (lock O-4).
//
// Three rules this module keeps:
//
//   1. The counts are the host's. `createSeatCount` is a physical obligation seat count read
//      from the service record, never slots multiplied by headcount in a browser. Five
//      services at headcount two is five services and ten seats, and the two never merge.
//   2. Nothing is trusted by shape alone. The review is only accepted when it echoes the
//      exact week it was asked for and every count is a safe non-negative integer, and only
//      blockers this build knows how to render are kept.
//   3. The review revision is opaque and is carried straight back to the publish act. It is
//      never parsed, never displayed, and never used as a locator.

/** The publish review states this build knows how to render. */
export const REVIEW_BLOCKERS = [
  "pattern_not_chosen",
  "pattern_retired",
  "pattern_not_publishable",
  "nothing_to_create",
  "officer_double_booked",
] as const;

export type ReviewBlocker = (typeof REVIEW_BLOCKERS)[number];

const REVIEW_BLOCKER_SET: ReadonlySet<string> = new Set(REVIEW_BLOCKERS);

/** Warnings never block. They are facts the operator should read before publishing. */
export const REVIEW_WARNINGS = ["uncovered_seat", "approval_required_seat"] as const;

export type ReviewWarningCode = (typeof REVIEW_WARNINGS)[number];

const REVIEW_WARNING_SET: ReadonlySet<string> = new Set(REVIEW_WARNINGS);

export interface ReviewWarning {
  readonly code: ReviewWarningCode;
  readonly count: number;
}

export interface SchedulePublishReview {
  /** The ISO Monday the host reviewed, echoed back. */
  readonly weekStart: string;
  /** Half-open end, so no caller has to guess whether the last day is included. */
  readonly weekEndExclusive: string;
  /** The site's own zone, or null when the host had no pattern to read one from. */
  readonly timeZone: string | null;
  /** Services this publish would create now. */
  readonly createSlotCount: number;
  /** Physical obligation seats those services would carry. */
  readonly createSeatCount: number;
  readonly existingSlotCount: number;
  /** Seats the service record already holds for this pattern week. */
  readonly existingSeatCount: number;
  /** Existing services at a candidate time that are cancelled and still block re-creation. */
  readonly cancelledBlockingCount: number;
  /** Ordered by the lock's priority. The surface renders the first one only. */
  readonly blockers: readonly ReviewBlocker[];
  readonly warnings: readonly ReviewWarning[];
  /** Opaque. Carried back to the publish act unread. */
  readonly reviewRevision: string;
}

export const SAFE_REVIEW_FAILURE = "Couldn't read this week's publish review. Please try again.";

export type SchedulePublishReviewOutcome =
  | { ok: true; review: SchedulePublishReview }
  | { ok: false; reason: string };

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isCount(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}

function isIsoDate(value: unknown): value is string {
  return typeof value === "string" && ISO_DATE.test(value);
}

function readWarnings(value: unknown): readonly ReviewWarning[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const warnings: ReviewWarning[] = [];
  for (const entry of value) {
    if (entry === null || typeof entry !== "object") {
      continue;
    }
    const row = entry as Record<string, unknown>;
    // An unknown warning code is dropped rather than rendered raw: this surface shows
    // sentences an operator can act on, not whatever a future migration starts emitting.
    if (typeof row.code !== "string" || !REVIEW_WARNING_SET.has(row.code) || !isCount(row.count)) {
      continue;
    }
    warnings.push({ code: row.code as ReviewWarningCode, count: row.count });
  }
  return warnings;
}

function readBlockers(value: unknown): readonly ReviewBlocker[] {
  if (!Array.isArray(value)) {
    return [];
  }
  // Host order is the lock's priority order and is preserved exactly. Unknown codes are
  // dropped, because a reason this build cannot phrase is worse than the generic gate.
  return value.filter((entry): entry is ReviewBlocker => typeof entry === "string" && REVIEW_BLOCKER_SET.has(entry));
}

function readReview(value: unknown, weekStart: string): SchedulePublishReview | null {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  const row = value as Record<string, unknown>;
  if (row.status !== "ok") {
    return null;
  }
  // The week is checked rather than assumed. A review that answers about a different week
  // than the one on screen is not a review, it is a mismatch, and it must not be rendered.
  if (row.weekStart !== weekStart || !isIsoDate(row.weekEndExclusive)) {
    return null;
  }
  if (
    !isCount(row.createSlotCount) ||
    !isCount(row.createSeatCount) ||
    !isCount(row.existingSlotCount) ||
    !isCount(row.existingSeatCount) ||
    !isCount(row.cancelledBlockingCount)
  ) {
    return null;
  }
  if (typeof row.reviewRevision !== "string" || !UUID.test(row.reviewRevision)) {
    return null;
  }
  return {
    weekStart,
    weekEndExclusive: row.weekEndExclusive,
    timeZone: typeof row.timeZone === "string" && row.timeZone.length > 0 ? row.timeZone : null,
    createSlotCount: row.createSlotCount,
    createSeatCount: row.createSeatCount,
    existingSlotCount: row.existingSlotCount,
    existingSeatCount: row.existingSeatCount,
    cancelledBlockingCount: row.cancelledBlockingCount,
    blockers: readBlockers(row.blockers),
    warnings: readWarnings(row.warnings),
    reviewRevision: row.reviewRevision,
  };
}

/**
 * Read the host's publish review for one pattern and one ISO Monday week. Never throws.
 *
 * `shiftTemplateId` may be null: the host answers with the pattern-not-chosen blocker rather
 * than an error, so the Review step can render its honest zero state from the same read.
 */
export async function fetchSchedulePublishReview(
  client: SupabaseClient,
  accountId: string,
  shiftTemplateId: string | null,
  weekStart: string,
): Promise<SchedulePublishReviewOutcome> {
  try {
    const { data, error } = await client.rpc("management_schedule_publish_review", {
      p_account_id: accountId,
      p_shift_template_id: shiftTemplateId,
      p_week_start: weekStart,
    });
    if (error !== null) {
      return { ok: false, reason: SAFE_REVIEW_FAILURE };
    }
    const review = readReview(data, weekStart);
    // Every host refusal is one shape on purpose, so this adapter cannot become an oracle
    // for whether the account, the pattern, or the permission is the thing that failed.
    return review === null ? { ok: false, reason: SAFE_REVIEW_FAILURE } : { ok: true, review };
  } catch {
    return { ok: false, reason: SAFE_REVIEW_FAILURE };
  }
}

export interface SchedulePublishReceipt {
  readonly weekStart: string;
  readonly weekEndExclusive: string;
  readonly timeZone: string | null;
  /** Services this act created. Zero on a duplicate submission. */
  readonly createdSlotCount: number;
  /** Physical obligation seats this act created. Never slots times headcount. */
  readonly createdSeatCount: number;
  readonly weekSlotCount: number;
  readonly weekSeatCount: number;
  /** The pattern's status after the act, when the host reported one. */
  readonly templateStatus: string | null;
}

export const SAFE_PUBLISH_WEEK_FAILURE = "Couldn't publish this week. Please try again.";

/**
 * The review moved between the read and the act, so nothing was written. The operator's next
 * move is to read the current review, which is what the surface offers.
 */
export const PUBLISH_REVIEW_CHANGED =
  "This week changed while you were reviewing it. Nothing was published. Check the new review.";

export type PublishWeekOutcome =
  | { ok: true; outcome: "created" | "already_published"; receipt: SchedulePublishReceipt }
  | { ok: false; kind: "conflict"; reason: string }
  | { ok: false; kind: "refused"; blocker: ReviewBlocker }
  | { ok: false; kind: "unavailable"; reason: string };

/**
 * Pattern statuses this build knows how to render beside a pattern name. An unknown status is
 * dropped to null and the surface keeps the status it already read, for the same reason an
 * unknown blocker is dropped: host text this build cannot phrase does not reach the DOM.
 */
const TEMPLATE_STATUSES: ReadonlySet<string> = new Set(["draft", "published", "retired"]);

function readReceipt(value: unknown, weekStart: string): SchedulePublishReceipt | null {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  const row = value as Record<string, unknown>;
  if (row.weekStart !== weekStart || !isIsoDate(row.weekEndExclusive)) {
    return null;
  }
  if (
    !isCount(row.createdSlotCount) ||
    !isCount(row.createdSeatCount) ||
    !isCount(row.weekSlotCount) ||
    !isCount(row.weekSeatCount)
  ) {
    return null;
  }
  return {
    weekStart,
    weekEndExclusive: row.weekEndExclusive,
    timeZone: typeof row.timeZone === "string" && row.timeZone.length > 0 ? row.timeZone : null,
    createdSlotCount: row.createdSlotCount,
    createdSeatCount: row.createdSeatCount,
    weekSlotCount: row.weekSlotCount,
    weekSeatCount: row.weekSeatCount,
    templateStatus:
      typeof row.templateStatus === "string" && TEMPLATE_STATUSES.has(row.templateStatus)
        ? row.templateStatus
        : null,
  };
}

/**
 * Publish one pattern into one ISO Monday week, against the revision the review issued.
 * Never throws.
 *
 * The revision is required. Without it the act is a conflict, which is the honest answer: a
 * publish nobody reviewed is not a publish the operator asked for.
 */
export async function publishShiftTemplateWeek(
  client: SupabaseClient,
  accountId: string,
  shiftTemplateId: string,
  weekStart: string,
  reviewRevision: string,
): Promise<PublishWeekOutcome> {
  try {
    const { data, error } = await client.rpc("publish_shift_template_week", {
      p_account_id: accountId,
      p_shift_template_id: shiftTemplateId,
      p_week_start: weekStart,
      p_expected_review_revision: reviewRevision,
    });
    if (error !== null) {
      return { ok: false, kind: "unavailable", reason: SAFE_PUBLISH_WEEK_FAILURE };
    }
    if (data === null || typeof data !== "object" || Array.isArray(data)) {
      return { ok: false, kind: "unavailable", reason: SAFE_PUBLISH_WEEK_FAILURE };
    }
    const row = data as Record<string, unknown>;
    if (row.status === "conflict") {
      return { ok: false, kind: "conflict", reason: PUBLISH_REVIEW_CHANGED };
    }
    if (row.status === "refused") {
      const blocker = typeof row.reason === "string" && REVIEW_BLOCKER_SET.has(row.reason) ? row.reason : null;
      return blocker === null
        ? { ok: false, kind: "unavailable", reason: SAFE_PUBLISH_WEEK_FAILURE }
        : { ok: false, kind: "refused", blocker: blocker as ReviewBlocker };
    }
    if (row.status !== "ok" || (row.outcome !== "created" && row.outcome !== "already_published")) {
      return { ok: false, kind: "unavailable", reason: SAFE_PUBLISH_WEEK_FAILURE };
    }
    const receipt = readReceipt(row.receipt, weekStart);
    if (receipt === null) {
      // The write may well have landed. Saying "published" without a receipt we can read
      // would put an unproven number on a green chip, so this reports the failure to READ
      // and leaves the operator on a surface whose next act is to re-read the review.
      return { ok: false, kind: "unavailable", reason: SAFE_PUBLISH_WEEK_FAILURE };
    }
    return { ok: true, outcome: row.outcome, receipt };
  } catch {
    return { ok: false, kind: "unavailable", reason: SAFE_PUBLISH_WEEK_FAILURE };
  }
}
